<template>
  <div class="employee-ack-summary-cards">
    <WorkflowSummaryCardsSkeleton
      v-if="isLoading"
      class="summary-cards-skeleton"
    />
    <template v-else>
      <WorkflowUpToDateCard
        :percentage="upToDatePct"
        :label="upToDateLabel"
        card-title="Up to date"
        icon="flsa-check-circle-outline"
        :selected="isCardSelected(['acknowledgement_ok'])"
        @click="handleCardClick(['acknowledgement_ok'], 'Up to date')"
      />
      <WorkflowSummaryCard
        v-for="card in summaryCards"
        :key="card.key"
        :icon="card.icon"
        :icon-class="card.iconClass"
        :border-class="card.borderClass"
        :label="card.label"
        :value="card.value"
        :tooltip="card.tooltip"
        :selected="isCardSelected(card.optionIds)"
        :compact="compactCards"
        @click="handleCardClick(card.optionIds, card.label)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { getTooltip } from '@/helpers/tooltips';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useAppBase } from '@/stores/app/base';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useRouter, useRoute } from 'vue-router';
import WorkflowSummaryCardsSkeleton from '@/components/library/shared/WorkflowSummaryCardsSkeleton.vue';
import WorkflowUpToDateCard from '@/components/library/shared/WorkflowUpToDateCard.vue';
import WorkflowSummaryCard from '@/components/library/shared/WorkflowSummaryCard.vue';
import { useTracking } from '@/stores/tracking/tracking';
import {
  ACKNOWLEDGEMENT_ALERT_ICONS,
  ACKNOWLEDGEMENT_ALERT_LABELS,
} from '@/helpers/acknowledgementAlerts';

const COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH = 1400;

const props = defineProps({
  totals: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const { selectAcknowledgementStatus, updateSelectedAcknowledgementStatuses } = useEmployeeAcknowledgementsStore();
const { selectedAcknowledgementStatuses } = storeToRefs(useEmployeeAcknowledgementsStore());
const { windowWidth } = storeToRefs(useAppBase());
const { open: filterDrawerOpen } = storeToRefs(useFilterDrawer());
const router = useRouter();
const route = useRoute();

const upToDatePct = computed(() => {
  const total = props.totals.total_employees || 0;

  if (!total) return 0;

  return Math.round(((props.totals.acknowledgement_ok || 0) / total) * 100);
});

const upToDateLabel = computed(() => {
  const total = props.totals?.total_employees || 0;

  if (!total) return '0%';

  const ok = props.totals?.acknowledgement_ok || 0;

  const rounded = Math.round((ok / total) * 100);

  if (ok > 0 && rounded === 0) return '<1%';

  return `${rounded}%`;
});

const compactCards = computed(() => (
  filterDrawerOpen.value && windowWidth.value < COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH
));

const handleCardClick = (optionIds, statusLabel) => {
  useTracking().ackLibSelectedMetricCard({ status: statusLabel });

  selectAcknowledgementStatus(optionIds, router);
};

const isCardSelected = (optionIds) => {
  const selected = [...selectedAcknowledgementStatuses.value].sort();
  const sorted = [...optionIds].sort();

  return selected.length === sorted.length
    && selected.every((id, i) => id === sorted[i]);
};

const summaryCards = computed(() => {
  const { totals = {} } = props;

  return [
    {
      key: 'no_acknowledgement',
      label: ACKNOWLEDGEMENT_ALERT_LABELS.no_acknowledgement,
      value: totals.no_acknowledgement || 0,
      borderClass: 'border-no-acknowledgement',
      iconClass: 'icon-no-acknowledgement',
      icon: ACKNOWLEDGEMENT_ALERT_ICONS.no_acknowledgement,
      optionIds: ['no_acknowledgement'],
      tooltip: getTooltip('acknowledgement-alert-no-ack'),
    },
    {
      key: 'pending_acknowledgement',
      label: ACKNOWLEDGEMENT_ALERT_LABELS.pending_acknowledgement,
      value: totals.pending_acknowledgement || 0,
      borderClass: 'border-pending',
      iconClass: 'icon-pending',
      icon: ACKNOWLEDGEMENT_ALERT_ICONS.pending_acknowledgement,
      optionIds: ['pending_acknowledgement'],
      tooltip: getTooltip('acknowledgement-alert-incomplete'),
    },
    {
      key: 'job_profile_changed',
      label: ACKNOWLEDGEMENT_ALERT_LABELS.job_profile_id_changed,
      value: (totals.job_profile_id_changed || 0) + (totals.job_profile_content_changed || 0),
      borderClass: 'border-job-profile-changed',
      iconClass: 'icon-job-profile-changed',
      icon: ACKNOWLEDGEMENT_ALERT_ICONS.job_profile_id_changed,
      optionIds: ['job_profile_id_changed', 'job_profile_content_changed'],
      tooltip: getTooltip('acknowledgement-alert-profile-changed'),
    },
    {
      key: 'acknowledgement_out_of_date',
      label: ACKNOWLEDGEMENT_ALERT_LABELS.acknowledgement_out_of_date,
      value: totals.acknowledgement_out_of_date || 0,
      borderClass: 'border-out-of-date',
      iconClass: 'icon-out-of-date',
      icon: ACKNOWLEDGEMENT_ALERT_ICONS.acknowledgement_out_of_date,
      optionIds: ['acknowledgement_out_of_date'],
      tooltip: getTooltip('acknowledgement-alert-out-of-date'),
    },
  ];
});

onMounted(() => {
  if (route.query?.acknowledgement_alert) {
    if (Array.isArray(route.query.acknowledgement_alert)) {
      updateSelectedAcknowledgementStatuses(route.query.acknowledgement_alert);
    } else {
      updateSelectedAcknowledgementStatuses(route.query.acknowledgement_alert.split(','));
    }
  }
});
</script>

<style lang="less" scoped>
.employee-ack-summary-cards {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  min-width: 0;
}

.summary-cards-skeleton {
  grid-column: 1 / -1;
  min-width: 0;
}
</style>
